import { customerFacingDescription } from './approval-state';
import { etDayMs, formatBusinessDateUS } from './date-utils';

/**
 * The one-line memo accounting pastes into QuickBooks:
 * `LOCATION - WO#31200 - MM/DD/YYYY - <customer description>`.
 *
 * Location leads because that is what the reader is scanning for — a unit or building code
 * identifies the job on a statement far faster than a date does, and QuickBooks truncates
 * from the right, so the identifying half has to come first.
 *
 * The date is the work order's COMPLETION day in Eastern time. `completedAt` is a real
 * instant, not a business-date marker, so it is collapsed to its ET calendar day first —
 * an evening completion must not print as tomorrow just because UTC has rolled over.
 *
 * The description is the customer-facing one, with the same fallback to maintenance's
 * note the invoice memo and the printed work order use, so QuickBooks never reads a
 * different sentence from what the property manager approved.
 */
export interface QuickBooksMemoInput {
	orderNumber?: number | null;
	jobLocation?: string | null;
	completedAt?: number | null;
	customerDescription?: string | null;
	workCompleted?: string | null;
}

export function quickBooksMemo(row: QuickBooksMemoInput): string {
	const location = (row.jobLocation ?? '').trim();
	const wo = row.orderNumber != null ? `WO#${row.orderNumber}` : 'WO#';
	const date = row.completedAt ? formatBusinessDateUS(etDayMs(row.completedAt), '') : '';
	const description = customerFacingDescription(row).replace(/\s+/g, ' ');
	return [location, wo, date, description].filter((part) => part !== '').join(' - ');
}
